import { cn } from '@/shared/helpers/classnames'
import { getTypedBlocksByTypes } from '@/shared/helpers/compoundPattern'
import { WithIcon } from '@/shared/ui/icon'

import { forwardRef } from 'react'

import type { InputBlocksTypes, InputIconProps, InputProps } from './types'

/**
 * Input.Icon component for displaying icons inside Input
 *
 * @example
 * ```tsx
 * <Input.Icon position="left">
 *   <Search />
 * </Input.Icon>
 * ```
 */
const InputIcon = ({ children, position = 'right' }: InputIconProps) => {
  return (
    <div
      className={cn('inputw-icon', {
        'inputw-icon-left': position === 'left',
        'inputw-icon-right': position === 'right',
      })}
    >
      {children}
    </div>
  )
}

InputIcon.displayName = 'InputIcon'

/**
 * Input component with error state support.
 *
 * A standard text input field using Tailwind CSS and design system variables.
 * Supports all native HTML input attributes except className (immutability).
 *
 * Features:
 * - Error state with visual feedback
 * - Bordered style by default
 * - Full width layout
 * - Ref forwarding for direct DOM access
 * - Type-safe props with TypeScript
 * - Icon support with Input.Icon sub-component
 *
 * @example
 * ```tsx
 * // Simple input
 * <Input
 *   type="text"
 *   placeholder="Enter your name"
 *   value={name}
 *   onChange={(e) => setName(e.target.value)}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Input with error state
 * <Input
 *   type="email"
 *   placeholder="Enter your email"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   error={!isEmailValid}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Input with icon
 * <Input type="text" placeholder="Search...">
 *   <Input.Icon position="left">
 *     <Search />
 *   </Input.Icon>
 * </Input>
 * ```
 */
const InputBase = forwardRef<HTMLInputElement, InputProps & { children?: React.ReactNode }>(
  ({ error, size = 'md', type = 'text', disabled, readOnly, children, ...props }, ref) => {
    // Extract icon using compound pattern utility
    const blocks = getTypedBlocksByTypes<InputBlocksTypes>(children)
    const icon = blocks.InputIcon
    const hasIcon = !!icon
    const iconPosition = icon?.props.position ?? 'right'

    const inputElement = (
      <input
        ref={ref}
        type={type}
        disabled={disabled}
        readOnly={readOnly}
        className={cn('inputw', {
          'inputw-error': error,
          'inputw-disabled': disabled,
          'inputw-readonly': readOnly,
          'inputw-sm': size === 'sm',
          'inputw-lg': size === 'lg',
          'inputw-with-icon': hasIcon,
          'inputw-with-icon-left': hasIcon && iconPosition === 'left',
          'inputw-with-icon-right': hasIcon && iconPosition === 'right',
        })}
        {...props}
      />
    )

    if (hasIcon && icon) {
      return (
        <div className="inputw-wrapper">
          <WithIcon icon={icon} position={iconPosition}>
            {inputElement}
          </WithIcon>
        </div>
      )
    }

    return inputElement
  },
)

InputBase.displayName = 'Input'

export const Input = Object.assign(InputBase, {
  Icon: InputIcon,
})
